/**
 * What an ability slot shows (AD-849, `q-ability-cooldown-visuals`; design
 * docs/design/mobile-layout.md §7.7): its cooldown as a readiness fill — the same bottom-up fill
 * the Dodge button draws from `status.dodgeReadyPct` — with the whole seconds left, and a dim when
 * the current stamina cannot pay its cost. Pure: every function takes `now` and returns new data,
 * so the rules are testable without timers.
 *
 * Server-authoritative: the cooldown comes from the server's own tick clock (`readyInMs` on
 * `abilityResult` and on each `loadoutState` ability), the cost from `loadoutState`, the stamina
 * from the live `staminaState` push. The client only interpolates the countdown between pushes and
 * never blocks a press on it — the server still judges every one.
 */

/** One ability's cooldown as the client last heard it: when it is ready, out of how long. */
export interface AbilityCooldown {
  /** `Date.now()` at which the ability is ready again. */
  readyAt: number;
  /** The full cooldown, ms (the fill's denominator). */
  cooldownMs: number;
}

/** Ability id → its running cooldown (absent ⇒ ready). */
export type AbilityCooldowns = Readonly<Record<string, AbilityCooldown>>;

/** The three looks a slot has. `cooling` wins over `starved` for the label; both can dim. */
export type AbilitySlotState = 'ready' | 'cooling' | 'starved';

export interface AbilitySlotView {
  state: AbilitySlotState;
  /** Readiness 0..1 — 1 = ready; the fill's height, as the Dodge button's. */
  readyPct: number;
  /** Whole seconds left (0 when ready) — the slot's label. */
  leftS: number;
  /** True while the current stamina is below the ability's cost. */
  starved: boolean;
}

/**
 * Arm (or re-arm) an ability's cooldown from a server answer: `readyInMs` left at `now`, out of
 * `cooldownMs` (the loadout's number; when unknown, the longest of what is known, so the fill
 * never starts above empty). A non-positive `readyInMs` clears it — the server says it is ready.
 */
export function armAbilityCooldown(
  cooldowns: AbilityCooldowns,
  abilityId: string,
  readyInMs: number,
  now: number,
  cooldownMs?: number,
): AbilityCooldowns {
  if (!(readyInMs > 0)) {
    if (!(abilityId in cooldowns)) return cooldowns;
    const rest: Record<string, AbilityCooldown> = { ...cooldowns };
    delete rest[abilityId];
    return rest;
  }
  const known = cooldownMs !== undefined && cooldownMs > 0 ? cooldownMs : 0;
  const prior = cooldowns[abilityId]?.cooldownMs ?? 0;
  return {
    ...cooldowns,
    [abilityId]: { readyAt: now + readyInMs, cooldownMs: Math.max(known || prior, readyInMs) },
  };
}

/**
 * The cooldowns a `loadoutState` push states: every listed ability with time left, nothing else
 * (the push is the whole truth for the loadout at that moment).
 */
export function cooldownsFromLoadout(
  abilities: readonly { id: string; cooldownMs: number; readyInMs: number }[],
  now: number,
): AbilityCooldowns {
  let out: AbilityCooldowns = {};
  for (const a of abilities) out = armAbilityCooldown(out, a.id, a.readyInMs, now, a.cooldownMs);
  return out;
}

/** The cooldown left at `now`, ms (0 = ready). */
export function cooldownLeftMs(
  cooldowns: AbilityCooldowns,
  abilityId: string,
  now: number,
): number {
  const c = cooldowns[abilityId];
  return c === undefined ? 0 : Math.max(0, c.readyAt - now);
}

/** True while any listed cooldown is still running at `now` (the HUD's redraw clock runs only then). */
export function anyCooling(cooldowns: AbilityCooldowns, now: number): boolean {
  return Object.values(cooldowns).some((c) => c.readyAt > now);
}

/**
 * The slot's look at `now`. `staminaCost` / `stamina` unknown ⇒ never starved (no dim on a guess).
 */
export function abilitySlotView(
  cooldowns: AbilityCooldowns,
  abilityId: string,
  now: number,
  staminaCost: number | undefined,
  stamina: number | undefined,
): AbilitySlotView {
  const left = cooldownLeftMs(cooldowns, abilityId, now);
  const total = cooldowns[abilityId]?.cooldownMs ?? 0;
  const readyPct = left <= 0 || total <= 0 ? 1 : Math.max(0, Math.min(1, 1 - left / total));
  const starved =
    staminaCost !== undefined && stamina !== undefined && staminaCost > 0 && stamina < staminaCost;
  return {
    state: left > 0 ? 'cooling' : starved ? 'starved' : 'ready',
    readyPct,
    leftS: left > 0 ? Math.ceil(left / 1000) : 0,
    starved,
  };
}

/**
 * The refusals an ability slot now shows itself (the sweep, the dim), so no toast is raised for
 * them (owner, 2026-10-23: "that would also remove the need for the text pop ups around
 * cooldowns"). Every other refusal — out of range, no target, not in the loadout, down, not in
 * play — still toasts: the slot has no look for it.
 */
export const SLOT_SHOWN_REFUSALS: ReadonlySet<string> = new Set([
  'onCooldown',
  'insufficientStamina',
]);

/** True when an ability refusal for `reason` still needs its toast. */
export function abilityRefusalToasts(reason: string | undefined): boolean {
  return reason === undefined || !SLOT_SHOWN_REFUSALS.has(reason);
}

/**
 * True when a basic-attack refusal for `reason` still needs its toast. `onCooldown` does not: a
 * tap or F pressed mid-swing is answered by the swing already in progress, and its "Attack:
 * onCooldown" line was the same cooldown clutter the owner asked to remove (measured: the only
 * refusal toast left in the first lane run of this story). `outOfRange`, `noTarget`, `dead` and
 * `notPlaying` still toast.
 */
export function attackRefusalToasts(reason: string | undefined): boolean {
  return reason !== 'onCooldown';
}
